// code adapted from here: https://github.com/visgl/deck.gl/blob/8.1-release/modules/core/bundle/deckgl.js
// but removing the dependency on the 'react-map-gl' component

import { Deck } from '@deck.gl/core'
import type { DeckProps, FilterContext, Layer } from '@deck.gl/core'
import maplibregl from 'maplibre-gl'
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { getGoogleMapInstance } from './google/google-maps.helper'
import { Basemap } from '@workspaces/types'
import { isGoogleMapsBasemap } from '@/helpers/google-maps.helper'
import { manageAssetsLayersVisibility } from '@/helpers/map.helper'
import { getAppMetadata } from '@/plan/metadata/metadata'
import { getZoomLevelForDeclustering } from '@/plan/metadata/metadata.helper'

export const ZOOM_TRANSITION_DURATION = 250

interface DeckMapViewState {
  longitude: number
  latitude: number
  zoom: number
  bearing?: number
  pitch?: number
}

export interface DeckMapProps extends DeckProps {
  container?: HTMLElement | string
  basemap?: Basemap.Basemap
  appBasemap?: Basemap.Basemap
  mapStyle?: string
  visibleBuffer?: boolean
}

interface BoundingBox {
  center: [number, number] | number[]
  zoom: number
  provider: Basemap.Basemap
}

interface MapRefLike {
  onUpdateViewState(boundingBox: BoundingBox): void
}

// `_map` is either a MapLibre Map or a deck.gl GoogleMapsOverlay.
type MaplibreMap = maplibregl.Map
type BaseMap = GoogleMapsOverlay | MaplibreMap | null

// maplibre-gl private members used by the manual-render workaround in
// `redrawMapbox`; not part of the public typings. MapLibre forked from
// mapbox-gl v1, so these internals carry over; the call site guards against
// them being absent in case a future version drops them.
interface MaplibreMapInternals {
  style?: unknown
  _frame: { cancel(): void } | null
  _render?: () => void
}

const CANVAS_STYLE = {
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
}

const DEFAULT_MAP_PROPS: DeckMapProps = {
  layers: [],
  basemap: undefined,
  controller: { scrollZoom: { smooth: true, speed: ZOOM_TRANSITION_DURATION } },
  useDevicePixels: 2,
  getCursor: ({ isDragging }) => (isDragging ? 'grabbing' : 'pointer'),
  getTooltip({ x, y, object, layer }) {
    if (!object) {
      return
    }
    // `getTooltip` is a per-layer prop the app stores on layers, not a standard deck prop.
    const tooltipFn = (layer?.props as Record<string, unknown>)?.getTooltip
    if (typeof tooltipFn === 'function') {
      return tooltipFn({ object, layer, x, y })
    }
  },
  layerFilter({ layer, viewport }: FilterContext) {
    // `layerFilter` is a per-layer prop the app stores on layers, not a standard deck prop.
    const filterFn = (layer.props as Record<string, unknown>).layerFilter
    if (typeof filterFn === 'function') {
      return filterFn(viewport, layer)
    }
    return true
  },
}

// Create canvas elements for mapbox and deck
function createCanvas(props: DeckMapProps) {
  const { container: containerProp = document.body } = props

  const container: HTMLElement | null =
    typeof containerProp === 'string'
      ? document.querySelector<HTMLElement>(containerProp)
      : containerProp

  if (!container) {
    throw new Error('[DeckMap] container not found')
  }

  const containerStyle = window.getComputedStyle(container)
  if (containerStyle.position === 'static') {
    container.style.position = 'relative'
  }

  const mapboxCanvas = document.createElement('div')
  container.appendChild(mapboxCanvas)
  Object.assign(mapboxCanvas.style, CANVAS_STYLE)

  const deckCanvas = document.createElement('canvas')
  deckCanvas.oncontextmenu = () => false // this disables right click on map
  container.appendChild(deckCanvas)
  Object.assign(deckCanvas.style, CANVAS_STYLE)

  return { container, mapboxCanvas, deckCanvas }
}

/**
 * @params container (Element) - DOM element to add deck.gl canvas to
 * @params map (Object) - map API. Set to falsy to disable
 */
export default class DeckMap extends Deck {
  appBasemap: Basemap.Basemap = Basemap.Basemap.OpenStreetMaps
  currentBoundingBox: BoundingBox | null = null
  mapRef: MapRefLike | null = null
  _map: BaseMap = null
  // deck.gl's render hooks are dynamically swapped in `setProps`. deck.gl 9
  // invokes onBeforeRender with { device, gl: WebGL2RenderingContext }; we keep
  // the param loose since we only forward it through.
  _onBeforeRender?: (params: unknown) => void
  onBeforeRender?: (params: unknown) => void

  constructor(props: DeckMapProps = {}) {
    props = {
      ...DEFAULT_MAP_PROPS,
      ...props,
    }

    if (props.appBasemap === Basemap.Basemap.OpenStreetMaps) {
      props.onResize = () => {
        if (this._map) {
          // OpenStreetMaps path: `_map` is the MapLibre Map.
          const map = this._map as MaplibreMap
          map.resize()
        }
      }
    }

    const { mapboxCanvas, deckCanvas } = createCanvas(props)

    super({ canvas: deckCanvas, ...props })

    const viewState = (props.viewState ||
      props.initialViewState) as DeckMapViewState
    this.appBasemap = props.basemap ?? Basemap.Basemap.OpenStreetMaps

    if (this.appBasemap === Basemap.Basemap.OpenStreetMaps) {
      this._map = new maplibregl.Map({
        container: mapboxCanvas,
        style: props.mapStyle,
        interactive: false,
        center: [viewState.longitude, viewState.latitude],
        zoom: viewState.zoom,
        bearing: viewState.bearing || 0,
        pitch: viewState.pitch || 0,
      })
    } else {
      this._map = null
    }

    if (this.appBasemap === Basemap.Basemap.OpenStreetMaps) {
      // Update base map
      this._onBeforeRender = (params) => {
        this.onBeforeRender?.(params)
        if (this._map) {
          // Viewport carries camera fields (longitude/latitude/bearing/pitch)
          // that are not on the base `Viewport` type.
          const viewport = this.getViewports()[0] as unknown as
            | DeckMapViewState
            | undefined
          // getViewports() is transiently empty on some frames (e.g. a redraw
          // forced before deck has recomputed the viewport). Guard it: reading
          // `viewport.longitude` when undefined throws inside onBeforeRender,
          // which aborts the whole frame — so the basemap sync fails AND deck's
          // own layer reconcile/draw for that frame is lost. See sc-557623.
          if (viewport) {
            // OpenStreetMaps path: `_map` is the MapLibre Map.
            const map = this._map as MaplibreMap
            map.jumpTo({
              center: [viewport.longitude, viewport.latitude],
              zoom: viewport.zoom,
              bearing: viewport.bearing,
              pitch: viewport.pitch,
            })
            // TODO: only redraw when viewport has changed
            this.redrawMapbox()
          }
        }
      }
      // Install the basemap-sync hook as deck's onBeforeRender. deck.gl 9 no
      // longer routes through the setProps() swap the way v8 did (the app never
      // passes onBeforeRender, so the swap condition was never met and the hook
      // stayed uninstalled → the MapLibre basemap never followed deck's view).
      // Setting it explicitly makes deck invoke it every frame.
      this.setProps({ onBeforeRender: this._onBeforeRender })
    }
  }

  setMapRef(mapRef: MapRefLike) {
    this.mapRef = mapRef
  }

  // deck.gl 9's viewState-transition clock is the luma Timeline, which only
  // advances on rendered frames (time += engineTime - lastEngineTime). deck
  // skips frames while nothing needs redrawing, so after an idle period the
  // first rendered frame applies the whole idle gap as elapsed time at once —
  // a transition started while idle is fast-forwarded by that gap (or finishes
  // instantly), which reads as the camera snapping instead of flying. Clearing
  // lastEngineTime makes the next frame contribute a zero delta (same trick as
  // Timeline.pause()), so a transition started now runs its full duration.
  resetTransitionClock() {
    const timeline = (
      this as unknown as {
        animationLoop?: { timeline?: { lastEngineTime: number } }
      }
    ).animationLoop?.timeline
    if (timeline) {
      timeline.lastEngineTime = -1
    }
  }

  // deck.gl 9's AnimationLoop does not render on its own here: setProps() and
  // Deck.redraw() only flag `_needsRedraw`, but nothing drives a frame while the
  // map is idle (no camera transition, no pointer interaction). So a layer
  // update issued while idle — e.g. excluding assets, which only changes layer
  // data, not the camera — is staged in `deck.props.layers` but never
  // reconciled or drawn, leaving the previous icons on screen. Manual use hides
  // this because continuous pointer events keep driving frames. Calling the
  // AnimationLoop's own `redraw()` forces the pending frame (reconcile + draw).
  //
  // Only call this on an idle update (no active camera transition): a forced
  // frame advances the transition clock (see resetTransitionClock), so forcing
  // redraws during a flyTo fast-forwards/cancels it. Deferred + coalesced onto
  // the next animation frame — invoking redraw() synchronously re-enters the
  // render cycle and crashes the renderer. See sc-557623 (deck.gl 9 migration).
  scheduleForcedRedraw() {
    const self = this as unknown as {
      _forcedRedrawScheduled?: boolean
      animationLoop?: { redraw?: () => void }
    }
    if (self._forcedRedrawScheduled) {
      return
    }
    self._forcedRedrawScheduled = true
    requestAnimationFrame(() => {
      self._forcedRedrawScheduled = false
      self.animationLoop?.redraw?.()
    })
  }

  getAppBasemap() {
    return this.appBasemap
  }

  updateCurrentBoundingBox(center: number[], zoom: number) {
    const provider = this.appBasemap
    this.currentBoundingBox = {
      center,
      zoom,
      provider,
    }
    this.mapRef?.onUpdateViewState(this.currentBoundingBox)
  }

  getCurrentBoundingBox() {
    return this.currentBoundingBox
  }

  isAppBasemapGoogleMaps() {
    return isGoogleMapsBasemap(this.appBasemap)
  }

  getBaseMap() {
    return this._map
  }

  async setGoogleMapsBaseMap(
    layers: Layer[],
    style: Basemap.Basemap,
    viewState: Record<string, unknown>,
  ) {
    this.appBasemap = style
    const viewStateParams =
      Object.keys(viewState).length > 0
        ? (viewState as unknown as Basemap.ViewState)
        : null
    const map = await getGoogleMapInstance(this.appBasemap, viewStateParams)
    const googleMaps = new GoogleMapsOverlay({ layers })
    googleMaps.setMap(map)
    this._map = googleMaps

    map.addListener('idle', () => {
      this.handleViewStateChange(map)
    })
  }

  handleViewStateChange(map: google.maps.Map) {
    const center = map.getCenter() as google.maps.LatLng
    const centerFormatted = [center.lng(), center.lat()]
    const zoom = map.getZoom() as number

    this.updateCurrentBoundingBox(centerFormatted, zoom)

    // console.debug('🌎 DECK: ViewState changed:', {
    //   center: centerFormatted,
    //   zoom,
    // })

    const metadata = getAppMetadata()
    manageAssetsLayersVisibility(getZoomLevelForDeclustering(metadata), zoom, {
      zoom,
    })
  }

  // code taken from here: https://github.com/visgl/react-map-gl/blob/ce6f6662ca34f8765cf0f515039e316adb52a957/src/mapbox/mapbox.js#L421
  // Force redraw the map now. Typically resize() and jumpTo() is reflected in the next
  // render cycle, which is managed by Mapbox's animation loop.
  // This removes the synchronization issue caused by requestAnimationFrame.
  redrawMapbox() {
    // Only invoked on the OpenStreetMaps basemap path; reaches into maplibre-gl
    // private internals.
    const map = this._map as MaplibreMap & MaplibreMapInternals
    // _render throws if style does not exist; only force a synchronous redraw
    // when both the style and the private _render hook are available.
    if (map.style && typeof map._render === 'function') {
      // cancel the scheduled update
      if (map._frame) {
        map._frame.cancel()
        map._frame = null
      }
      // the order is important - render() may schedule another update
      map._render()
    }
  }

  getMapboxMap() {
    return this._map
  }

  finalize() {
    const prefixMsg = '🌎 DECK: Finalizing DeckMap instance'
    if (this.isAppBasemapGoogleMaps()) {
      console.debug(prefixMsg, ' with basemap Google Maps')
      ;(this._map as GoogleMapsOverlay).setMap(null)
    } else {
      if (this._map) {
        console.debug(prefixMsg, ' with basemap OpenStreetMaps')
        ;(this._map as MaplibreMap).remove()
      }
    }
    super.finalize()
  }

  setProps(props: DeckMapProps) {
    // Replace user callback with our own
    // `setProps` is first called in parent class constructor
    // During which this._onBeforeRender is not defined
    // It is called a second time in _onRendererInitialized with all current props
    if (
      'onBeforeRender' in props &&
      this._onBeforeRender &&
      props.onBeforeRender !== this._onBeforeRender
    ) {
      this.onBeforeRender = props.onBeforeRender as (params: unknown) => void
      props.onBeforeRender = this
        ._onBeforeRender as DeckMapProps['onBeforeRender']
    }

    super.setProps(props)
  }
}
